import { type ReactElement, type ReactNode, Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { Hammer } from 'lucide-react';
import LoginRoute from './components/auth/LoginRoute';
// T1172: Marketplace — pot se mora ujemati z `menu.json.path` v manifestu.
import DashboardLayout from './components/layout/DashboardLayout';
import { RequireModuleSeat } from './components/guards/RequireModuleSeat';
import { QUANTIFY_MODULE } from './components/ui/SeatGateNotice';
import CookieBanner from './components/layout/CookieBanner';
import SessionExpiredDialog from './components/auth/SessionExpiredDialog';
import TestEnvironmentBanner from './components/layout/TestEnvironmentBanner';
import LanguageSwitcher from './components/LanguageSwitcher';
import { blockAllowsBilling } from './lib/orgBlock';
import { useAuthStore } from './store/authStore';
import { twoFactorSetupPath } from './lib/twoFactor';
import { loginPathWithNext } from './lib/safeNext';
import { useOrgBlockStore } from './store/orgBlockStore';
import { useSystemStore } from './store/systemStore';
import { useUIStore } from './store/uiStore';
import { useBrowserContext } from './hooks/useBrowserContext';

/* eslint-disable react-refresh/only-export-components -- lene strani, ne izvozi */
// T2883: strani se nalagajo leno (React.lazy) — začetni chunk nosi samo lupino,
// prijavo in pristajalno stran; ostalo pride ob prvem obisku poti.
// T3108: tudi pristajalna stran in zaslon zaklenjene organizacije — prijavljen
// uporabnik prve ne vidi, druga je redka.
const LandingPage = lazy(() => import('./pages/LandingPage'));
const OrganizationBlockedScreen = lazy(() => import('./components/layout/OrganizationBlockedScreen'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const WaitlistPage = lazy(() => import('./pages/WaitlistPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const ProjectsListPage = lazy(() => import('./pages/ProjectsListPage'));
const ProjectLayout = lazy(() => import('./pages/ProjectLayout'));
const ProjectDashboardPage = lazy(() => import('./pages/ProjectDashboardPage'));
const ProjectSettingsPage = lazy(() => import('./pages/ProjectSettingsPage'));
const BoQEditorPage = lazy(() => import('./pages/BoQEditorPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const MembersPage = lazy(() => import('./pages/MembersPage'));
const SysadminPage = lazy(() => import('./pages/SysadminPage'));
const SysadminModulesPage = lazy(() => import('./pages/SysadminModulesPage'));
const SysadminUsersPage = lazy(() => import('./pages/SysadminUsersPage'));
const SysadminFinancePage = lazy(() => import('./pages/SysadminFinancePage'));
const UnitsOfMeasurePage = lazy(() => import('./pages/sysadmin/UnitsOfMeasurePage'));
const SysadminStoragePage = lazy(() => import('./pages/sysadmin/SysadminStoragePage'));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const LibraryPage = lazy(() => import('./pages/LibraryPage'));
const MarketplacePage = lazy(() => import('./pages/marketplace/MarketplacePage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const OrganizationReopenPage = lazy(() => import('./pages/OrganizationReopenPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const EmailVerifyPage = lazy(() => import('./pages/EmailVerifyPage'));
const EmailChangePage = lazy(() => import('./pages/EmailChangePage'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
const SharedProjectPage = lazy(() => import('./pages/SharedProjectPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage'));
const DemoGalleryPage = lazy(() => import('./pages/DemoGalleryPage'));
const IFCMappingPage = lazy(() => import('./pages/boq/IFCMappingPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
/* eslint-enable react-refresh/only-export-components */

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background" role="status" aria-live="polite">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[var(--bp-accent)]"></div>
  </div>
);

/** T3000: pot, poizvedba in hash — vse, kar mora preživeti prijavo. */
const fullPath = (l: { pathname: string; search: string; hash: string }) =>
  l.pathname + l.search + l.hash;

const ProtectedRoute = ({ children }: { children: ReactElement }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) return null;

  // T3000: cilj gre v `?next=`, ne v `state.from`. Tega ni bral nihče —
  // prijavna stran, pot `/login` (T2519) in okno ponovne prijave (T2376) berejo
  // `next` —, zato je globoka povezava iz e-pošte po prijavi pristala na
  // `/dashboard`. Naslov preživi tudi osvežitev strani, nov zavihek in korak
  // 2FA; `loginPathWithNext` ga spusti skozi isti `safeNextPath` kot branje.
  if (!isAuthenticated) {
    return <Navigate to={loginPathWithNext(fullPath(location))} replace />;
  }

  return children;
};

const SysadminRoute = ({ children }: { children: ReactElement }) => {
  const { user, isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) return null;

  if (!isAuthenticated || !user?.is_site_admin) {
    return <Navigate to="/projects" replace />;
  }

  // T2816: sysadmin mora imeti dvostopenjsko prijavo, preden dobi Sysadmin.
  if (user.two_factor?.setup_required) {
    // T3000: cilj (npr. `?tab=waitlist` iz e-pošte) preživi vmesno nastavitev.
    return <Navigate to={twoFactorSetupPath(fullPath(location))} replace />;
  }

  return children;
};

function App() {
  const { t } = useTranslation();
  const { checkAuth, isLoading, isAuthenticated, user } = useAuthStore();
  const { settings, fetchSettings } = useSystemStore();
  const { isDarkMode } = useUIStore();
  const orgBlock = useOrgBlockStore((s) => s.block);
  const location = useLocation();

  useBrowserContext();

  useEffect(() => {
    checkAuth();
    fetchSettings();
  }, []); // Only check once on mount

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[var(--bp-accent)]"></div>
          <p className="text-caption font-medium text-muted-foreground uppercase tracking-widest">{t('common.welcome')}</p>
          <div className="mt-2">
            <LanguageSwitcher variant="light" />
          </div>
        </div>
      </div>
    );
  }

  // T2689: okno za ponovno prijavo (T2376) je priklopljeno v VSEH treh vejah —
  // običajno drevo, vzdrževanje in zaklep organizacije — in vedno na istem
  // mestu (drugi otrok zunanjega fragmenta), zato ga preklop veje ne odklopi.
  // Odklop bi odjavil rokovalnik prestreznika: zahteve, ki že čakajo na okno,
  // bi obvisele, z njimi pa vsaka nadaljnja 401 (`isRefreshing` ostane `true`).
  const withSessionDialog = (screen: ReactNode) => (
    <>
      {screen}
      <SessionExpiredDialog />
    </>
  );

  // Handle Maintenance Mode — allow /login, /register, /waitlist through so sysadmin can authenticate
  const AUTH_PATHS = ['/login', '/register', '/waitlist'];
  if (settings?.is_maintenance_mode && !user?.is_site_admin && !AUTH_PATHS.includes(location.pathname)) {
    return withSessionDialog(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[var(--bp-surface-2)]">
        <div className="max-w-md w-full p-8 text-center space-y-6">
          <div className="bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 p-4 rounded-full w-fit mx-auto">
            <Hammer className="h-10 w-10" />
          </div>
          <div className="space-y-2">
            <h1 className="text-display font-bold text-[var(--bp-text-1)]">{t('app.maintenance_title')}</h1>
            <p className="text-[var(--bp-text-2-soft)] leading-relaxed">
              {t('app.maintenance_desc')}
            </p>
          </div>
          <div className="pt-4 border-t border-[var(--bp-border)] space-y-4">
            <p className="text-caption text-muted-foreground uppercase tracking-widest font-semibold">
              {t('app.maintenance_status')}
            </p>
            <a
              href="/login"
              className="inline-block text-dense text-muted-foreground hover:text-[var(--bp-text-2)] underline underline-offset-2 transition-colors"
            >
              {t('app.maintenance_admin_login')}
            </a>
          </div>
          <div className="pt-2">
            <LanguageSwitcher variant="light" />
          </div>
        </div>
      </div>
    );
  }

  // T1192: organizacija je zaklenjena (403 + ORGANIZATION_SUSPENDED/_CLOSED).
  // Prevzem je globalen iz istega razloga kot pri načinu vzdrževanja: zaprt je
  // praktično ves API, zato bi vsaka stran sicer pokazala svojo naključno
  // napako. Site admin je izvzet — backend ga v blokado sploh ne spusti, tu pa
  // še enkrat, da mu zaslon nikoli ne skoči.
  //
  // Plačilna izjema velja SAMO tam, kjer je plačilo res pot iz zaklepa —
  // merilo je zato isto kot zaledno in živi v `blockAllowsBilling()`
  // (`lib/orgBlock.ts`). T1629: prej je bila izjema vezana samo na KODO, zato
  // je pri razlogu `sysadmin`, kjer backend zapre cel `billing` namespace,
  // lastnik dobil izrisan `/billing` s trinajstimi 403 in sporočilom, da ni
  // lastnik.
  const allowsBilling = blockAllowsBilling(orgBlock, location.pathname);
  if (orgBlock && isAuthenticated && !user?.is_site_admin && !allowsBilling) {
    return withSessionDialog(
      <Suspense fallback={<RouteFallback />}>
        <OrganizationBlockedScreen block={orgBlock} />
      </Suspense>,
    );
  }

  return withSessionDialog(
    <>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        {/* T2519: prijavljenega z `/login` vrne na varen `?next=` (vabilo, tržnica). */}
        <Route path="/login" element={<LoginRoute />} />
        <Route
          path="/register"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />}
        />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route
          path="/waitlist"
          element={isAuthenticated ? <Navigate to="/projects" replace /> : <WaitlistPage />}
        />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        {/* T1349: cilj povezave iz sporočila lastniku zaprtega računa.
            Javna pot — uporabnik zaprte organizacije se ne more prijaviti,
            zato mora pot ven obstajati pred sejo (kot reset gesla). */}
        <Route path="/organization-reopen/:token" element={<OrganizationReopenPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/email-verify/:token" element={<EmailVerifyPage />} />
        {/* T2910: povezavi iz e-pošte za spremembo e-naslova — javni, kot reset gesla. */}
        <Route path="/email-change/cancel/:token" element={<EmailChangePage mode="cancel" />} />
        <Route path="/email-change/:token" element={<EmailChangePage mode="confirm" />} />
        <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
        <Route path="/share/:token" element={<SharedProjectPage />} />

        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          {/* T2436: brez sedeža za Quantify ni dostopa do projektov — namesto
              praznega seznama ali »projekt ne obstaja« stran z razlago. */}
          <Route element={<RequireModuleSeat slug={QUANTIFY_MODULE} />}>
            <Route path="/projects" element={<ProjectsListPage />} />
            {/* T1474: trije zasloni projekta si delijo lupino (glava + zavihki).
                Prej sta bili `boq` in `dashboard` samostojni ruti, vsaka s svojo
                ročno sestavljeno glavo — in ti dve glavi sta se že razšli. */}
            <Route path="/projects/:id" element={<ProjectLayout />}>
              <Route index element={<Navigate to="boq" replace />} />
              <Route path="boq" element={<BoQEditorPage />} />
              <Route path="dashboard" element={<ProjectDashboardPage />} />
              <Route path="settings" element={<ProjectSettingsPage />} />
            </Route>
            {/* IFC preslikava ostane SESTRSKA ruta, ne otrok: bere `:projectId`,
                ne `:id`, in je celozaslonski čarovnik brez zavihkov projekta.
                Gnezdenje bi ji `useParams().projectId` spremenilo v `undefined`. */}
            <Route path="/projects/:projectId/ifc/:uploadId/mapping" element={<IFCMappingPage />} />
          </Route>
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          {/* T3144: Moduli so zavihek Nastavitev; stara pot ostane za povezave iz pošte in zaznamke. */}
          <Route path="/settings/modules" element={<Navigate to="/settings?tab=modules" replace />} />
          <Route path="/members" element={<MembersPage />} />
          <Route
            path="/sysadmin"
            element={
              <SysadminRoute>
                <SysadminPage />
              </SysadminRoute>
            }
          />
          <Route
            path="/sysadmin/modules"
            element={
              <SysadminRoute>
                <SysadminModulesPage />
              </SysadminRoute>
            }
          />
          <Route
            path="/sysadmin/users"
            element={
              <SysadminRoute>
                <SysadminUsersPage />
              </SysadminRoute>
            }
          />
          <Route
            path="/sysadmin/finance"
            element={
              <SysadminRoute>
                <SysadminFinancePage />
              </SysadminRoute>
            }
          />
          <Route
            path="/sysadmin/units"
            element={
              <SysadminRoute>
                <UnitsOfMeasurePage />
              </SysadminRoute>
            }
          />
          <Route
            path="/sysadmin/storage"
            element={
              <SysadminRoute>
                <SysadminStoragePage />
              </SysadminRoute>
            }
          />

          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/billing/success" element={<BillingPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          {/* T1085: Help center — celovita dokumentacija za vse module. */}
          <Route path="/docs" element={<HelpCenterPage />} />
          {/* T1134: galerija animiranih vodnikov — statična pot pred /docs/:context. */}
          <Route path="/docs/animacije" element={<DemoGalleryPage />} />
          <Route path="/docs/:context" element={<HelpCenterPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/messages/:conversationId" element={<MessagesPage />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      <TestEnvironmentBanner />
      <CookieBanner />
    </>
  );
}

// T2899: lastna meja napak namesto `Sentry.withErrorBoundary` — Sentry se naloži
// po prvem izrisu, napaka iz meje pa počaka nanj v vrsti (`lib/sentryLazy.ts`).
function AppWithErrorBoundary() {
  return (
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  );
}

export default AppWithErrorBoundary;
